import React, { useState } from "react";

const UK_CITIES = [
  "Bath", "Birmingham", "Bradford", "Brighton", "Bristol",
  "Cambridge", "Canterbury", "Carlisle", "Chelmsford", "Chester",
  "Chichester", "Coventry", "Derby", "Durham", "Ely", "Exeter",
  "Gloucester", "Hereford", "Kingston upon Hull", "Lancaster",
  "Leeds", "Leicester", "Lichfield", "Lincoln", "Liverpool",
  "London", "Manchester", "Newcastle upon Tyne", "Norwich",
  "Nottingham", "Oxford", "Peterborough", "Plymouth", "Portsmouth",
  "Preston", "Ripon", "Salford", "Salisbury", "Sheffield",
  "Southampton", "Stoke-on-Trent", "Sunderland", "Truro",
  "Wakefield", "Wells", "Westminster", "Winchester", "Wolverhampton",
  "Worcester", "York",
  "Aberdeen", "Dundee", "Edinburgh", "Glasgow", "Inverness", "Perth", "Stirling",
  "Bangor", "Cardiff", "Newport", "St Davids", "Swansea",
  "Armagh", "Belfast", "Derry", "Lisburn", "Newry",
];

const SocialPage = () => {
  const [city, setCity] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);

  const fetchEvents = async () => {
    if (!city) return;
    setLoading(true);
    setError("");
    setEvents([]);
    setSearched(false);

    try {
      const res = await fetch(`/api/open/social-events?city=${encodeURIComponent(city)}`);
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Failed to load events.");
      } else {
        setEvents(data.events || []);
        setSearched(true);
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      weekday: "short", day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f9fafb", padding: "40px 16px" }}>
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        <h1 style={{ fontSize: 32, fontWeight: 700, color: "#111827", marginBottom: 8 }}>
          Social Events
        </h1>
        <p style={{ color: "#6b7280", marginBottom: 32, fontSize: 16 }}>
          Discover events happening in your city via Eventbrite.
        </p>

        <div style={{ display: "flex", gap: 12, marginBottom: 40, flexWrap: "wrap" }}>
          <select
            value={city}
            onChange={(e) => setCity(e.target.value)}
            style={{
              flex: "1 1 220px",
              padding: "10px 14px",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              fontSize: 15,
              backgroundColor: "#fff",
              color: city ? "#111827" : "#9ca3af",
              outline: "none",
            }}
          >
            <option value="">Select a UK city...</option>
            {UK_CITIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <button
            onClick={fetchEvents}
            disabled={!city || loading}
            style={{
              padding: "10px 24px",
              backgroundColor: city && !loading ? "#4f46e5" : "#a5b4fc",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 15,
              fontWeight: 600,
              cursor: city && !loading ? "pointer" : "not-allowed",
              transition: "background-color 0.2s",
            }}
          >
            {loading ? "Searching..." : "Find Events"}
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: "#fee2e2", border: "1px solid #fca5a5",
            borderRadius: 8, padding: "12px 16px", color: "#b91c1c",
            marginBottom: 24,
          }}>
            {error}
          </div>
        )}

        {loading && (
          <div style={{ textAlign: "center", color: "#6b7280", padding: 40 }}>
            Loading events...
          </div>
        )}

        {searched && !loading && events.length === 0 && (
          <div style={{ textAlign: "center", color: "#6b7280", padding: 40 }}>
            No upcoming events found in {city}.
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {events.map((event) => (
            <a
              key={event.id}
              href={event.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                gap: 16,
                backgroundColor: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                padding: 20,
                textDecoration: "none",
                color: "inherit",
                transition: "box-shadow 0.2s",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"; }}
            >
              {event.logo && (
                <img
                  src={event.logo}
                  alt={event.name ? `${event.name} logo` : "Event logo"}
                  style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8, flexShrink: 0 }}
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#111827", lineHeight: 1.4 }}>
                    {event.name}
                  </h3>
                  {event.is_free && (
                    <span style={{
                      flexShrink: 0, fontSize: 12, fontWeight: 600,
                      color: "#059669", backgroundColor: "#d1fae5",
                      padding: "2px 8px", borderRadius: 99,
                    }}>
                      Free
                    </span>
                  )}
                </div>
                {event.start && (
                  <p style={{ margin: "4px 0 0", fontSize: 13, color: "#4f46e5", fontWeight: 500 }}>
                    {formatDate(event.start)}
                  </p>
                )}
                {event.venue && (
                  <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>
                    {event.venue}
                  </p>
                )}
                {event.description && (
                  <p style={{ margin: "8px 0 0", fontSize: 14, color: "#374151", lineHeight: 1.5 }}>
                    {event.description}
                  </p>
                )}
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SocialPage;
